<script setup lang="ts">
import { useDeckPlayerContext } from '../../composables/useDeckPlayerShell'

/** Card-sized start screen: deck title and per-phase progress bars. */
const player = useDeckPlayerContext()
const { props, preStartPhases, preStartStyle, phaseSegmentColors: getPhaseSegmentColors } = player
const { previewEffectiveScale } = player.presentation
</script>

<template>
  <div class="pre-start" :style="preStartStyle" :data-pfc-scale="previewEffectiveScale">
    <!-- Row 1: deck title (left) + coins (right) -->
    <div class="pre-start__header">
      <div class="pre-start__title">{{ props.deckTitle }}</div>
    </div>
    <!-- Row 2: FORTSCHRITT + phase bars -->
    <div v-if="preStartPhases.length > 0" class="pre-start__body">
      <div class="pre-start__fortschritt">FORTSCHRITT</div>
      <div class="pre-start__phases-list">
        <div
          v-for="(phase, pi) in preStartPhases"
          :key="phase.phaseId || pi"
          class="pre-start__phase-row"
        >
          <div class="pre-start__phase-name">{{ phase.label }}</div>
          <div class="pre-start__phase-bar">
            <div
              v-for="(col, si) in getPhaseSegmentColors(phase)"
              :key="si"
              class="pre-start__segment"
              :style="{ background: col }"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pre-start {
  /* Exact pixel size is set by preStartStyle (matches flashcard dimensions) */
  background: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: calc(var(--pre-start-scale, 1) * 10px);
  padding: calc(var(--pre-start-scale, 1) * 14px) calc(var(--pre-start-scale, 1) * 22px);
  box-sizing: border-box;
  overflow: hidden;
}

.pre-start__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: calc(var(--pre-start-scale, 1) * 8px);
  flex-wrap: nowrap;
  min-width: 0;
}

.pre-start__title {
  flex: 1 1 0;
  min-width: 0;
  font-size: calc(var(--pre-start-scale, 1) * 18px);
  font-weight: 900;
  color: #1a6ba8;
  text-transform: uppercase;
  line-height: 1.2;
  word-break: break-word;
  overflow-wrap: break-word;
}

.pre-start__rewards {
  display: flex;
  align-items: center;
  gap: calc(var(--pre-start-scale, 1) * 10px);
  flex-shrink: 0;
}

.pre-start__reward {
  display: flex;
  align-items: center;
  gap: calc(var(--pre-start-scale, 1) * 6px);
  flex-shrink: 0;
}

.pre-start__reward-img {
  height: auto;
  flex-shrink: 0;
  display: block;
}

.pre-start__reward-img--coins {
  width: calc(var(--pre-start-scale, 1) * 44px);
}

.pre-start__reward-img--pearl {
  width: calc(var(--pre-start-scale, 1) * 34px);
  height: auto;
  filter: drop-shadow(0 3px 4px rgba(9, 42, 64, 0.28));
}

.pre-start__reward-count {
  background: #f5a623;
  border-radius: calc(var(--pre-start-scale, 1) * 6px);
  padding: calc(var(--pre-start-scale, 1) * 3px) calc(var(--pre-start-scale, 1) * 9px);
  font-size: calc(var(--pre-start-scale, 1) * 14px);
  font-weight: 800;
  color: #fff;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: calc(var(--pre-start-scale, 1) * 26px);
  text-align: center;
}

.pre-start__reward--pearls .pre-start__reward-count {
  background: #8aa7bd;
}

.pre-start__body {
  display: flex;
  flex-direction: column;
  gap: calc(var(--pre-start-scale, 1) * 5px);
}

.pre-start__fortschritt {
  text-align: center;
  font-size: calc(var(--pre-start-scale, 1) * 10px);
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #374151;
  text-transform: uppercase;
}

.pre-start__phases-list {
  display: flex;
  flex-direction: column;
  gap: calc(var(--pre-start-scale, 1) * 9px);
}

.pre-start__phase-row {
  display: flex;
  align-items: center;
  gap: calc(var(--pre-start-scale, 1) * 9px);
}

.pre-start__phase-name {
  font-size: calc(var(--pre-start-scale, 1) * 8px);
  font-weight: 700;
  color: #4b5563;
  min-width: calc(var(--pre-start-scale, 1) * 36px);
  white-space: nowrap;
  text-align: right;
  flex-shrink: 0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.pre-start__phase-bar {
  flex: 1;
  display: flex;
  height: calc(var(--pre-start-scale, 1) * 18px);
  border-radius: calc(var(--pre-start-scale, 1) * 4px);
  overflow: hidden;
  min-width: 0;
}

.pre-start__segment {
  flex: 1;
}
</style>
